import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, CircularProgress, CssBaseline, GlobalStyles, IconButton, Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  acceptCookieConsent,
  getStoredThemeMode,
  getTheme,
  hasCookieConsent,
  persistThemeMode,
} from './theme';
import CookieBanner from './components/CookieBanner';
import ThemeToggle from './components/ThemeToggle';
import {
  clearMishtameretSession,
  fetchMe,
  getMishtameretToken,
  setPortalSessionMode,
} from './mishtameretPortalApi';
import { buildTopNav, resolveActiveNavKey } from './nav';
import { getStationThemeMode, isStationPath, portalPath } from './stationMode';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';

const PortalShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStation = isStationPath(location.pathname);
  const basePath = portalPath(isStation);
  const loginPath = portalPath(isStation, '/login');
  // Sync before any token read so personal/station keys do not leak across modes
  setPortalSessionMode(isStation ? 'station' : 'personal');
  const [mode, setMode] = useState(() =>
    isStation ? getStationThemeMode() : getStoredThemeMode()
  );
  const [consent, setConsent] = useState(() =>
    isStation ? true : hasCookieConsent()
  );
  const [member, setMember] = useState(null);
  const [booting, setBooting] = useState(true);
  const theme = getTheme(mode);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const isTakingExam = location.pathname.includes('/exams/take/');
  const topNav = useMemo(() => buildTopNav(isStation), [isStation]);
  const activeNavKey = useMemo(
    () => resolveActiveNavKey(location.pathname, searchParams),
    [location.pathname, searchParams]
  );

  useEffect(() => {
    let link = document.getElementById('mishtameret-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'mishtameret-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = FONT_HREF;
    document.documentElement.style.background = theme.htmlBg;
    document.body.style.background = theme.htmlBg;
  }, [theme.htmlBg]);

  useEffect(() => {
    setPortalSessionMode(isStation ? 'station' : 'personal');
    let cancelled = false;
    (async () => {
      if (!getMishtameretToken()) {
        setBooting(false);
        return;
      }
      try {
        const data = await fetchMe();
        if (!cancelled) setMember(data.member);
      } catch {
        clearMishtameretSession();
        if (!cancelled) setMember(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isStation]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      if (!isStation && hasCookieConsent()) persistThemeMode(next);
      return next;
    });
  }, [isStation]);

  const handleAcceptCookies = () => {
    if (isStation) return;
    acceptCookieConsent();
    setConsent(true);
    persistThemeMode(mode);
  };

  const logout = () => {
    clearMishtameretSession();
    navigate(loginPath, { replace: true });
  };

  if (!getMishtameretToken() && !booting) {
    return <Navigate to={loginPath} replace />;
  }

  if (booting) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: theme.pageBg }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  if (!member) {
    return <Navigate to={loginPath} replace />;
  }

  const outletContext = { theme, mode, member, onMemberChange: setMember, toggleTheme, isStation, basePath };

  return (
    <>
      <CssBaseline />
      <GlobalStyles styles={{ body: { margin: 0 }, html: { background: theme.htmlBg } }} />
      <Box
        sx={{
          minHeight: '100dvh',
          background: theme.pageBg,
          color: theme.text,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: theme.fontBody,
        }}
      >
        <Box
          component="header"
          sx={{
            borderBottom: `1px solid ${theme.borderSoft}`,
            bgcolor: theme.navBg,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 2, sm: 3 },
              py: 1.25,
              maxWidth: 1100,
              mx: 'auto',
              width: '100%',
              minHeight: 72,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 0.35,
                px: 7,
                minWidth: 0,
              }}
            >
              <Box
                component="img"
                src={theme.logo}
                alt=""
                sx={{ width: { xs: 40, sm: 48 }, height: 'auto' }}
              />
              <Typography
                sx={{
                  fontFamily: theme.fontDisplay,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                }}
              >
                תורתם משתמרת
              </Typography>
              <Typography
                sx={{
                  color: theme.textMuted,
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {member.firstName} {member.lastName}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: { xs: 8, sm: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                gap: 0.5,
              }}
            >
              <ThemeToggle mode={mode} theme={theme} onToggle={toggleTheme} />
              <IconButton onClick={logout} aria-label="יציאה" sx={{ color: theme.textMuted, width: 44, height: 44 }}>
                <LogoutIcon />
              </IconButton>
            </Box>
          </Box>

          {!isTakingExam ? (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pb: 1.5,
                maxWidth: 1100,
                mx: 'auto',
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: theme.borderSoft,
                  borderRadius: 999,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  width: 'max-content',
                  minWidth: '100%',
                  mx: 'auto',
                }}
              >
                {topNav.map((item) => {
                  const active = activeNavKey === item.key;
                  return (
                    <Box
                      key={item.key}
                      component="button"
                      type="button"
                      onClick={() => navigate(item.path)}
                      aria-pressed={active}
                      sx={{
                        flexShrink: 0,
                        px: { xs: 1.75, sm: 2.25 },
                        py: 1,
                        minHeight: 42,
                        borderRadius: 999,
                        border: `1px solid ${active ? theme.border : theme.borderSoft}`,
                        bgcolor: active ? theme.accent : 'transparent',
                        color: active ? theme.accentContrast : theme.text,
                        cursor: 'pointer',
                        fontFamily: theme.fontBody,
                        fontWeight: active ? 700 : 600,
                        fontSize: { xs: '0.9rem', sm: '0.95rem' },
                        letterSpacing: '0.01em',
                        transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          bgcolor: active ? theme.accentSoft : theme.surfaceHover,
                          borderColor: theme.border,
                        },
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2.5, md: 3.5 },
            maxWidth: 960,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Outlet context={outletContext} />
        </Box>

        {!isStation && !consent ? (
          <CookieBanner theme={theme} onAccept={handleAcceptCookies} />
        ) : null}
      </Box>
    </>
  );
};

export default PortalShell;
